import type { playerTagsEn } from './en.ts'

/**
 * Russian locale catalog for the player-tags sub-domain.
 *
 * Source of truth: `playerTagsEn` (`./en.ts`). Russian must satisfy
 * `typeof playerTagsEn` exactly so the type system catches every
 * structural drift between the two catalogs.
 *
 * Translation notes:
 *   - Tag names are persisted data; the catalogue only translates the
 *     SYSTEM chrome that surrounds them (button labels, placeholders,
 *     aria labels, modal copy).
 *   - Player names pass through verbatim as `playerDisplayName(player)`
 *     arguments — they are not translated here.
 *   - The destructive `confirmBody` uses the canonical Russian objective
 *     labels for the eight forest positions. The persisted default tag
 *     `tree` remains verbatim because tag names are never translated.
 */
export const playerTagsRu: typeof playerTagsEn = {
  filterStrip: {
    toolbarLabel: 'Фильтр по тегам',
    filterRosterChip: 'Фильтр состава',
    clearFilterButton: 'Сбросить фильтр',
  },
  playerStrip: {
    toggleTitle: (tagName: string) => `Фильтровать по тегу '${tagName}'`,
    toggleAria: (tagName: string) => `Переключить фильтр для тега ${tagName}`,
    removeTitle: (tagName: string, playerLabel: string) =>
      `Удалить тег ${tagName} у игрока ${playerLabel}`,
    removeAria: (tagName: string, playerLabel: string) =>
      `Удалить тег ${tagName} у игрока ${playerLabel}`,
    addTitle: (playerLabel: string) => `Назначить тег игроку ${playerLabel}`,
    addPendingTitle: 'Назначение тега…',
    triggerLabelIdle: '+ Тег',
    triggerLabelPending: 'Назначение…',
    dropdownLabel: 'Доступные теги',
  },
  catalog: {
    heading: 'Теги игроков',
    description: 'Свободные метки, которые можно закрепить за игроками (до 100 на гильдию).',
    resetHeading: 'Сбросить все назначения тегов',
    resetDescription:
      'Удаляет все назначения тегов и пользовательские теги, сохраняя системные теги карты.',
    resetTitleNoPermissions: 'Только офицеры могут сбрасывать назначения тегов',
    resetTitlePending: 'Сброс выполняется…',
    resetTitleEmpty: 'Удалить пользовательские теги и сбросить все назначения',
    resetTitleWithAssignments: (count: number) =>
      `Удалить все пользовательские теги и ${String(count)} ${count === 1 ? 'назначение' : count >= 2 && count <= 4 ? 'назначения' : 'назначений'}`,
    resetButtonIdle: 'Сбросить все теги',
    resetButtonPending: 'Сброс…',
    confirmTitle: 'Сбросить теги игроков?',
    confirmDestructiveLabel: 'Необратимое действие',
    confirmBody: (count: number) =>
      `Это удалит ${String(count)} ${count === 1 ? 'назначение' : count >= 2 && count <= 4 ? 'назначения' : 'назначений'} и безвозвратно удалит все пользовательские теги. Системные теги карты, включая боссов на 15 и 25 минуте, сохранятся.`,
    confirmCancel: 'Отмена',
    confirmDelete: 'Удалить пользовательские теги и сбросить',
    errorFallback: 'Не удалось сбросить назначения тегов. Попробуйте снова.',
    emptyCatalog: 'Тегов пока нет. Создайте тег выше, а затем закрепите его за игроком на его карточке.',
    createInputPlaceholder: 'Имя нового тега (напр. ⚔️ снайпер)',
    createInputLabel: 'Имя нового тега',
    createColorLabel: 'Цвет нового тега',
    createButton: 'Создать',
    emojiPickerLabel: 'Добавить эмодзи в новый тег',
    rowColorLabel: (tagName: string) => `Изменить цвет тега ${tagName}`,
    rowRenameLabel: (tagName: string) => `Переименовать тег ${tagName}`,
    rowDeleteLabel: (tagName: string) => `Удалить тег ${tagName}`,
    rowRenameButton: 'Переименовать…',
    rowDeleteButton: 'Удалить',
    renamePrompt: (currentName: string) => `Переименовать тег "${currentName}" в:`,
    deleteConfirmWithAssignments: (name: string, count: number) =>
      `Удалить тег "${name}"? Он будет убран у ${String(count)} ${count === 1 ? 'игрока' : count >= 2 && count <= 4 ? 'игроков' : 'игроков'}.`,
    deleteConfirmNoAssignments: (name: string) => `Удалить тег "${name}"?`,
    rowMeta: (color: string, count: number) =>
      `${color} · ${String(count)} ${count === 1 ? 'игрок' : count >= 2 && count <= 4 ? 'игрока' : 'игроков'}`,
  },
}
